import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ContentStatus } from 'src/app/shared/enums/content-status';
import { StatusConfig } from './interfaces/status-config';
import { IArticleContentDto } from 'src/app/core/dtos/interfaces/i-article-content-dto';
import { IPageContentDto } from '../../../core/dtos/interfaces/i-page-content-dto';
import { Observable } from 'rxjs';
import {
  SMART_LINK_ALREADY_RUNNING_HINT,
  SMART_LINK_READ_ONLY_HINT,
  SMART_LINK_READY_HINT,
  SMART_LINK_SAVE_FIRST_HINT,
} from 'src/app/shared/services/smart-linking-state.service';
import { SmartLinkTriggersService } from 'src/app/shared/services/smart-link-triggers.service';

@Component({
  selector: 'app-content-status-card',
  templateUrl: './content-status-card.component.html',
  styleUrls: ['./content-status-card.component.scss'],
})
export class ContentStatusCardComponent {
  @Input() form!: FormGroup;
  @Input() formReady!: Observable<boolean>;
  @Input() content!: IArticleContentDto;
  @Input() isSaving!: boolean;
  @Input() isLoading!: boolean;
  // Article-only, like isLoading: the five other content types sharing this card bind neither.
  @Input() isSmartLinkingEnabled = false;
  @Input() isEditMode = false;
  @Output() handleSave = new EventEmitter<void>();

  STATUS_CONFIGS: {
    [key: string]: StatusConfig;
  } = {
    [ContentStatus.NEW]: {
      title: () => 'New',
      cssClasses: () => [
        'border-project-1000',
        'bg-project-900',
        'text-project-1000',
      ],
    },
    [ContentStatus.SPIKED]: {
      title: () => 'Spiked',
      cssClasses: () => [
        'border-project-3500',
        'bg-project-3400/40',
        'text-project-3500',
      ],
    },
    [ContentStatus.READ_ONLY]: {
      title: () => 'Read only',
      cssClasses: () => [
        'border-project-50',
        'bg-project-600',
        'text-project-50',
      ],
    },
    [ContentStatus.EDITING]: {
      title: () => 'Editing',
      cssClasses: () => [
        'border-project-1000',
        'bg-project-900',
        'text-project-1000',
      ],
    },
    [ContentStatus.JOURNALIST_EDITING]: {
      title: () => 'Journalist editing',
      cssClasses: () => [
        'border-project-1000',
        'bg-project-900',
        'text-project-1000',
      ],
    },
    [ContentStatus.PUBLISHED]: {
      title: () =>
        this.content?.isCurrentLiveVersion()
          ? 'Published'
          : 'Former Published Version',
      cssClasses: () =>
        this.content?.isCurrentLiveVersion()
          ? ['border-project-3300', 'bg-project-3000', 'text-project-3300']
          : ['border-project-50', 'bg-project-600', 'text-project-50'],
    },
    [ContentStatus.WAITING_FOR_EDITING]: {
      title: () => 'Waiting to be edited',
      cssClasses: () => [
        'border-project-3200',
        'bg-project-2800',
        'text-project-3200',
      ],
    },
    [ContentStatus.WAITING_FOR_JOURNALIST_EDITING]: {
      title: () => 'Waiting for journalist to edit',
      cssClasses: () => [
        'border-project-3200',
        'bg-project-2800',
        'text-project-3200',
      ],
    },
    [ContentStatus.WRITING]: {
      title: () => 'Writing',
      cssClasses: () => [
        'border-project-1000',
        'bg-project-900',
        'text-project-1000',
      ],
    },
    [ContentStatus.WAITING_FOR_SUBEDITING]: {
      title: () => 'Waiting for sub-editing',
      cssClasses: () => [
        'border-project-3200',
        'bg-project-2800',
        'text-project-3200',
      ],
    },
    [ContentStatus.SUBEDITING]: {
      title: () => 'Subediting',
      cssClasses: () => [
        'border-project-1000',
        'bg-project-900',
        'text-project-1000',
      ],
    },
    [ContentStatus.SUBEDITOR_APPROVED]: {
      title: () => 'Sub-editor approved',
      cssClasses: () => [
        'border-project-3200',
        'bg-project-2800',
        'text-project-3200',
      ],
    },
  };

  constructor(private smartLinkTriggers: SmartLinkTriggersService) {}

  get statusConfigs(): StatusConfig {
    if (!this.content.id || !this.content.contentId) {
      return this.STATUS_CONFIGS[ContentStatus.NEW];
    }

    if (this.content.isSpiked) {
      return this.STATUS_CONFIGS[ContentStatus.SPIKED];
    }
    if (this.content.status) {
      return this.STATUS_CONFIGS[this.content.status];
    }

    if (
      this.content.dynamicData &&
      !this.content.dynamicData.canEdit &&
      !this.content.isPublished()
    ) {
      return this.STATUS_CONFIGS[ContentStatus.READ_ONLY];
    }
  }

  // The only thing the bar asks the registry. Availability below is decided from this card's own
  // inputs: a registry answer that changed as panels register would flip a binding mid-pass.
  get isSmartLinkRunning(): boolean {
    return this.smartLinkTriggers.isAnyBusy();
  }

  // No longer gated on form.dirty: runAll() passes skipSave (see onRunSmartLinks()), and each
  // registered trigger decides for itself whether a dirty article can run.
  get isSmartLinkDisabled(): boolean {
    return (
      !this.isEditMode ||
      !this.content?.contentId ||
      this.isSmartLinkRunning ||
      this.isSaving ||
      this.isLoading
    );
  }

  // Same order as the panel button's own title, so two buttons for one run never explain the same
  // refusal differently.
  get smartLinkTitle(): string {
    if (!this.isEditMode) {
      return SMART_LINK_READ_ONLY_HINT;
    }
    if (!this.content?.contentId) {
      return SMART_LINK_SAVE_FIRST_HINT;
    }
    if (this.isSmartLinkRunning) {
      return SMART_LINK_ALREADY_RUNNING_HINT;
    }
    if (this.isSaving || this.isLoading) {
      return 'Wait for the save to finish';
    }
    return SMART_LINK_READY_HINT;
  }

  onRunSmartLinks(): void {
    this.smartLinkTriggers.runAll({ skipSave: true });
  }
}
